import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API, getAuthHeaders } from '../shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plane, Users, Calendar, ChevronLeft, AlertCircle } from 'lucide-react';

const TYPE_FILTERS = [
  { id: 'all', label: 'الكل' },
  { id: 'حج', label: '🕋 حج' },
  { id: 'عمرة', label: '🕋 عمرة' },
  { id: 'سياحية', label: '🌴 سياحية' },
  { id: 'أخرى', label: '✈️ أخرى' }
];

const STATUS_FILTERS = [
  { id: 'all', label: 'الكل' },
  { id: 'active', label: 'نشط' },
  { id: 'upcoming', label: 'قادم' },
  { id: 'full', label: 'ممتلئ' }
];

const TravelGroupsWidget = ({ onNavigate }) => {
  useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const r = await axios.get(`${API}/umrah/dashboard/stats`, { headers: getAuthHeaders() });
        setGroups(r.data || []);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const filtered = groups.filter(g => {
    if (typeFilter !== 'all' && g.trip_type !== typeFilter) return false;
    if (statusFilter === 'active' && (g.is_full || (g.departure_date && g.departure_date < today))) return false;
    if (statusFilter === 'upcoming' && g.departure_date && g.departure_date <= today) return false;
    if (statusFilter === 'full' && !g.is_full) return false;
    return true;
  });

  if (loading) return <Card><CardContent className="py-8 text-center text-gray-500">جاري تحميل الأفواج...</CardContent></Card>;

  return (
    <Card className="border-r-4 border-r-purple-500">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-purple-600" />
            🛫 حالة الأفواج ({filtered.length}/{groups.length})
          </span>
          {onNavigate && (
            <Button size="sm" variant="ghost" onClick={() => onNavigate('umrah-groups')} className="text-xs">
              عرض الكل <ChevronLeft className="h-3 w-3 mr-1" />
            </Button>
          )}
        </CardTitle>
        {/* Filters */}
        <div className="flex gap-1 flex-wrap pt-2">
          {TYPE_FILTERS.map(t => (
            <Button key={t.id} size="sm" variant={typeFilter === t.id ? 'default' : 'outline'}
              onClick={() => setTypeFilter(t.id)}
              className="h-6 px-2 text-xs">{t.label}</Button>
          ))}
        </div>
        <div className="flex gap-1 flex-wrap pt-1">
          {STATUS_FILTERS.map(s => (
            <Button key={s.id} size="sm" variant={statusFilter === s.id ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s.id)}
              className="h-6 px-2 text-xs">{s.label}</Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            لا توجد أفواج بهذه المعايير
          </div>
        ) : (
          filtered.slice(0, 6).map(g => {
            const occColor = g.is_full ? 'bg-red-500' : g.occupancy_pct > 80 ? 'bg-amber-500' : 'bg-green-500';
            return (
              <div key={g.id} className="border rounded-lg p-3 hover:bg-gray-50 transition cursor-pointer"
                onClick={() => onNavigate && onNavigate('umrah-groups')}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{g.name}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <Calendar className="h-3 w-3" />
                      {g.departure_date}
                      <Badge variant="outline" className="text-xs h-4 px-1">{g.trip_type}</Badge>
                    </div>
                  </div>
                  {g.is_full ? (
                    <Badge className="bg-red-100 text-red-700 border-red-300">ممتلئ</Badge>
                  ) : (
                    <Badge variant="outline" className="font-mono">{g.remaining_seats} متبقي</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Users className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-600 font-mono">{g.used_seats}/{g.total_seats}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className={`h-full ${occColor} transition-all`} style={{ width: `${g.occupancy_pct}%` }} />
                  </div>
                  <span className="text-gray-600 font-mono">{g.occupancy_pct}%</span>
                </div>
                {g.bookings_count > 0 && (
                  <div className="text-xs text-brand-600 mt-1">
                    📋 {g.bookings_count} حجز رحلة منظمة
                  </div>
                )}
              </div>
            );
          })
        )}
        {filtered.length > 6 && (
          <p className="text-xs text-gray-400 text-center pt-2">+ {filtered.length - 6} أفواج أخرى</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TravelGroupsWidget;
